import React, { useState, useRef } from 'react'
import { PriorityTag } from './PriorityTag.jsx'
import { AuthorPill } from './AuthorPill.jsx'

export function AgendaItem({
  item,
  locked,
  currentUserId,
  otherUserName,
  onToggleChecked,
  onSetPriority,
  onSetText,
  onCommit,
  onRemove,
  dragHandleProps,
  isDragging,
  isDragOver,
}) {
  const [hovered, setHovered] = useState(false)
  const inputRef = useRef(null)

  const isAuthor = item.addedBy === currentUserId
  const canRemove = !locked && isAuthor

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 8px',
        borderRadius: 6,
        opacity: item.checked ? 0.4 : isDragging ? 0.4 : 1,
        background: isDragOver ? '#faf0e8' : 'transparent',
        transition: 'opacity var(--duration-fast) ease, background var(--duration-fast) ease',
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Drag handle */}
      {!locked && !item.checked && (
        <span
          {...dragHandleProps}
          style={{
            cursor: 'grab',
            color: 'var(--text-secondary, #aaa)',
            fontSize: 10,
            opacity: hovered ? 0.7 : 0,
            transition: 'opacity var(--duration-fast) ease',
            flexShrink: 0,
            userSelect: 'none',
            lineHeight: 1,
          }}
          title="Drag to reorder"
        >
          ⠿
        </span>
      )}
      {locked && !item.checked && <span style={{ width: 14 }} />}

      {/* Checkbox */}
      <button
        onClick={() => !locked && onToggleChecked(item.id)}
        disabled={locked}
        style={{
          width: 16,
          height: 16,
          borderRadius: 3,
          border: item.checked ? 'none' : '1.5px solid #d4c4b0',
          background: item.checked ? '#c4783b' : 'transparent',
          cursor: locked ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          padding: 0,
        }}
        aria-label={item.checked ? 'Uncheck item' : 'Check item'}
      >
        {item.checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 3.5L3.8 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Text */}
      <input
        ref={inputRef}
        type="text"
        value={item.text}
        onChange={(e) => !locked && onSetText(item.id, e.target.value)}
        onBlur={() => onCommit?.(item.id)}
        onKeyDown={(e) => { if (e.key === 'Enter') inputRef.current?.blur() }}
        autoFocus={item._isNew}
        readOnly={locked}
        maxLength={200}
        placeholder="Add topic…"
        style={{
          flex: 1,
          fontSize: 12,
          fontWeight: 400,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'var(--text-primary)',
          textDecoration: item.checked ? 'line-through' : 'none',
          cursor: locked ? 'default' : 'text',
          minWidth: 0,
        }}
      />

      {/* Meta row (priority, author, carry) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <PriorityTag
          priority={item.priority}
          onChange={(p) => onSetPriority(item.id, p)}
          disabled={locked || item.checked}
        />
        <AuthorPill
          addedBy={item.addedBy}
          currentUserId={currentUserId}
          otherUserName={otherUserName}
        />
        {/* Remove button */}
        {canRemove && hovered && (
          <button
            onClick={() => onRemove(item.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary, #aaa)',
              fontSize: 14,
              lineHeight: 1,
              padding: '0 2px',
              flexShrink: 0,
            }}
            aria-label="Remove item"
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}
